import React, { useState, useEffect } from 'react';
import { featureService, Feature } from '../../services/featureService';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import IconSelector from '../ui/IconSelector';
import LoadingSpinner from '../ui/LoadingSpinner';
import * as HeroIcons from '@heroicons/react/24/outline';

// Dynamic icon rendering based on string name
const DynamicIcon = ({ iconName, className }: { iconName: string, className?: string }) => {
  // @ts-ignore - We're dynamically accessing properties
  const IconComponent = HeroIcons[iconName.split('-').map(part => 
    part.charAt(0).toUpperCase() + part.slice(1)).join('') + 'Icon'];

  if (!IconComponent) {
    return <HeroIcons.StarIcon className={className} />;
  }

  return <IconComponent className={className} />;
};

export const FeatureManager: React.FC = () => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'star',
    active: true
  });

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      setIsLoading(true);
      const data = await featureService.getAllFeatures();
      setFeatures(data);
      setError(null);
    } catch (err) {
      setError('Failed to load features');
      console.error('Error loading features:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleIconChange = (icon: string) => {
    setFormData(prev => ({
      ...prev,
      icon
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      setError(null);
      
      if (editingId) {
        // Update existing feature
        await featureService.updateFeature(editingId, formData);
      } else {
        // Add new feature
        // Calculate next order number
        const nextOrder = features.length > 0 
          ? Math.max(...features.map(f => f.order)) + 1 
          : 0;
        
        await featureService.addFeature({
          ...formData,
          order: nextOrder
        });
      }
      
      await loadFeatures();
      resetForm();
    } catch (err) {
      setError('Failed to save feature');
      console.error('Error saving feature:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (feature: Feature) => {
    setEditingId(feature.id);
    setFormData({
      title: feature.title,
      description: feature.description,
      icon: feature.icon,
      active: feature.active
    });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this feature?')) {
      return;
    }
    
    try {
      setIsSaving(true);
      await featureService.deleteFeature(id);
      await loadFeatures();
    } catch (err) {
      setError('Failed to delete feature');
      console.error('Error deleting feature:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      title: '',
      description: '',
      icon: 'star',
      active: true
    });
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source } = result;
    
    // If dropped outside a droppable area or no change in position
    if (!destination || destination.index === source.index) {
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Reorder the items in our state
      const reorderedFeatures = Array.from(features);
      const [movedItem] = reorderedFeatures.splice(source.index, 1);
      reorderedFeatures.splice(destination.index, 0, movedItem);
      
      // Update our UI optimistically
      setFeatures(reorderedFeatures);
      
      // Update the order in the database
      const orderedIds = reorderedFeatures.map(feature => feature.id);
      await featureService.updateFeatureOrder(orderedIds);
    } catch (err) {
      // Reload from the database if there's an error
      console.error('Error updating order:', err);
      await loadFeatures();
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Why Choose Us Features</h2>
        <div className="text-sm text-gray-500">
          {features.length} {features.length === 1 ? 'Feature' : 'Features'} • 
          {features.filter(f => f.active).length} Active
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Feature Form */}
      <form onSubmit={handleSubmit} className="mb-8 border rounded-lg p-4">
        <h3 className="text-lg font-medium mb-4">
          {editingId ? 'Edit Feature' : 'Add New Feature'}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="title">
              Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
              required
            />
          </div>
          
          <IconSelector value={formData.icon} onChange={handleIconChange} />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="description">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
            required
          />
        </div>
        
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="active"
            name="active"
            checked={formData.active}
            onChange={handleInputChange}
            className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
          />
          <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
            Active
          </label>
        </div>
        
        <div className="flex justify-end space-x-2">
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              disabled={isSaving}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : editingId ? 'Update Feature' : 'Add Feature'}
          </button>
        </div>
      </form>

      {/* Feature List */}
      <h3 className="text-lg font-medium mb-4">Manage Features</h3>
      <p className="text-sm text-gray-500 mb-4">
        Drag and drop to reorder features. Features are displayed in the order shown below.
      </p>
      
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="features">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-3"
            >
              {features.length === 0 ? (
                <div className="text-center p-8 border border-dashed rounded-md">
                  <p className="text-gray-500">No features added yet. Add your first feature above.</p>
                </div>
              ) : (
                features.map((feature, index) => (
                  <Draggable key={feature.id} draggableId={feature.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`border rounded-md p-4 ${
                          snapshot.isDragging ? 'bg-accent-50' : 'bg-white'
                        } ${!feature.active ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div {...provided.dragHandleProps} className="cursor-move pt-1">
                              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zM13 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                              </svg>
                            </div>
                            
                            <div className="w-10 flex-shrink-0">
                              <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center">
                                <DynamicIcon iconName={feature.icon} className="h-5 w-5 text-accent-600" />
                              </div>
                            </div>
                            
                            <div className="flex-grow">
                              <h4 className="text-lg font-medium text-primary-900">
                                {feature.title}
                              </h4>
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {feature.description}
                              </p>
                              <div className="mt-2 flex items-center text-xs text-gray-500">
                                <span className={`px-2 py-1 rounded-full ${
                                  feature.active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {feature.active ? 'Active' : 'Inactive'}
                                </span>
                                <span className="ml-2">Order: {feature.order}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex space-x-2">
                            <button
                              type="button"
                              onClick={() => handleEdit(feature)}
                              className="text-gray-400 hover:text-primary-600"
                              aria-label="Edit"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(feature.id)}
                              className="text-gray-400 hover:text-red-600"
                              aria-label="Delete"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}; 